import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { companyInfo, contactContent } from '@/data/mock';
import { toast } from 'sonner';
import Seo from '@/components/Seo';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formPayload = new FormData();
      formPayload.append('access_key', '18980aa7-6be3-443b-a52c-2ca950519aad');
      formPayload.append('subject', 'New contact form submission');
      formPayload.append('name', formData.name);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone || 'N/A');
      formPayload.append('service', formData.service || 'Not selected');
      formPayload.append('message', formData.message);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formPayload,
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Submission failed.');
      }

      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");

      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Seo
        title="Contact"
        description="Contact yBro for review management, GBP support, local SEO, and compliance guidance."
        path="/contact"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-ybro-light/50 to-white py-24 lg:py-32">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-ybro-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">Contact Us</span>
            <h1 className="text-4xl lg:text-5xl font-bold font-heading text-slate-900 mb-6">
              {contactContent.title}
            </h1>
            <p className="text-xl text-slate-500 leading-relaxed font-body">
              {contactContent.description}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold font-heading text-slate-900 mb-8">Get in touch</h2>
              
              <div className="space-y-6">
                <Card className="border-slate-200 hover:border-ybro-primary/40 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-ybro-light flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-ybro-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold font-heading text-slate-900 mb-1">Email</h3>
                        <a 
                          href={`mailto:${companyInfo.email}`}
                          className="text-slate-500 hover:text-ybro-primary transition-colors duration-300 font-body"
                        >
                          {companyInfo.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 hover:border-ybro-primary/40 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-ybro-light flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-ybro-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold font-heading text-slate-900 mb-1">Phone</h3>
                        <a 
                          href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                          className="text-slate-500 hover:text-ybro-primary transition-colors duration-300 font-body"
                        >
                          {companyInfo.phone}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 hover:border-ybro-primary/40 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-ybro-light flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-ybro-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold font-heading text-slate-900 mb-1">Location</h3>
                        <p className="text-slate-500 font-body">
                          yBro Limited, 124 City Road, London, EC1V 2NX, UK
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200 shadow-xl shadow-ybro-primary/5">
                <CardContent className="p-8 lg:p-10">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-ybro-light flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-8 h-8 text-ybro-primary" />
                      </div>
                      <h3 className="text-2xl font-bold font-heading text-slate-900 mb-2">Message Sent!</h3>
                      <p className="text-slate-500 font-body">We'll get back to you as soon as possible.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium font-body text-slate-700 mb-2">
                            Name *
                          </label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Your name"
                            className="h-12 font-body"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium font-body text-slate-700 mb-2">
                            Email *
                          </label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="your@email.com"
                            className="h-12 font-body"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium font-body text-slate-700 mb-2">
                            Phone (optional)
                          </label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+44 ..."
                            className="h-12 font-body"
                          />
                        </div>
                        <div>
                          <label htmlFor="service" className="block text-sm font-medium font-body text-slate-700 mb-2">
                            Interested Service
                          </label>
                          <Select value={formData.service} onValueChange={handleServiceChange}>
                            <SelectTrigger className="h-12 font-body">
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              {contactContent.serviceOptions.map((option) => (
                                <SelectItem key={option} value={option} className="font-body">
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium font-body text-slate-700 mb-2">
                          Message *
                        </label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Tell us about your business and what you're looking for..."
                          rows={6}
                          className="resize-none font-body"
                        />
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full bg-ybro-primary hover:bg-ybro-dark text-white shadow-lg shadow-ybro-primary/25 hover:shadow-ybro-primary/40 transition-all duration-300 h-14 text-base font-body"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColour" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColour" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            Send Message
                            <Send className="w-5 h-5" />
                          </span>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
